/**
 * One-shot hand-off for the `signed_in` analytics event on the OAuth path.
 *
 * `authClient.signIn.social()` navigates the tab away immediately, so an
 * event fired next to that call may never flush. Instead the sign-in page
 * writes a flag here just before redirecting, and PostHogProvider consumes it
 * once the OAuth callback lands us back on the app with an identified
 * session. Consuming REMOVES the flag, so a subsequent page refresh does not
 * re-emit the event.
 *
 * sessionStorage (not localStorage) on purpose: the flag must die with the
 * tab, never leak into a later session.
 */

/** Wire key. Pinned by tests in sign-in/page.test.tsx and posthog-provider.test.tsx. */
export const PENDING_SIGNIN_METHOD_KEY = "bb_pending_signin_method";

export type PendingSignInMethod = "google";

/** Record that a social sign-in redirect is about to start. */
export function markPendingSignInMethod(method: PendingSignInMethod): void {
  try {
    sessionStorage.setItem(PENDING_SIGNIN_METHOD_KEY, method);
  } catch {
    // Storage disabled (private mode, blocked cookies) — analytics is
    // best-effort and must never break sign-in.
  }
}

/** Read and clear the flag. Returns null when there is nothing pending. */
export function consumePendingSignInMethod(): PendingSignInMethod | null {
  try {
    const value = sessionStorage.getItem(PENDING_SIGNIN_METHOD_KEY);
    if (value === null) {
      return null;
    }
    sessionStorage.removeItem(PENDING_SIGNIN_METHOD_KEY);
    return value === "google" ? "google" : null;
  } catch {
    return null;
  }
}

/**
 * Clear the flag without reading it, for a Google attempt that never reaches
 * a signed-in session in this tab (network error, beta-gate rejection, user
 * cancels on Google's consent screen). Without this, a stale flag from an
 * abandoned Google attempt gets picked up by PostHogProvider after a later,
 * unrelated email sign-in, double-counting and mislabeling `signed_in`.
 */
export function clearPendingSignInMethod(): void {
  try {
    sessionStorage.removeItem(PENDING_SIGNIN_METHOD_KEY);
  } catch {
    // Storage disabled (private mode, blocked cookies) — analytics is
    // best-effort and must never break sign-in.
  }
}
